import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  CanActivateChild,
  Router,
  UrlTree
} from '@angular/router';
import { Observable, TimeoutError, zip, of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { filter, map, timeout, catchError, first } from 'rxjs/operators';
import { DigiAIXCoreAppState } from '../app-state';
import { LoadDocuments, SubscribeDocuments } from './document.actions';
import { isCollectionLoaded } from './document.selectors';
import { NGXLogger } from 'ngx-logger';
import { CurrentCollectionPathsService } from './current-collection-paths.service';
import { LifeCycleService } from '../life-cycle/life-cycle.service';

@Injectable({
  providedIn: 'root'
})
export class CollectionLoadedGuard<AppState extends DigiAIXCoreAppState, Query>
  implements CanActivate, CanActivateChild {
  constructor(
    public store$: Store<AppState>,
    public router: Router,
    public logger: NGXLogger,
    public currentCollectionPaths: CurrentCollectionPathsService<Query>,
    public lifeCycle: LifeCycleService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.lifeCycle.whenStable(() => {
      const load = this.currentCollectionPaths.get(next);

      if (!load || !load.length) {
        this.logger.debug(
          '[CollectionLoadedGuard] current collection paths empty'
        );
        return of(true);
      } else {
        this.logger.debug(
          '[CollectionLoadedGuard] current collection paths',
          load
        );
      }

      for (const params of load) {
        this.store$
          .pipe(
            select(isCollectionLoaded(params.path)),
            first(),
            map(il =>
              il
                ? new SubscribeDocuments(
                    params.path,
                    params.query,
                    '[CollectionLoadedGuard::canActivate]'
                  )
                : new LoadDocuments(
                    params.path,
                    params.query,
                    '[CollectionLoadedGuard::canActivate]'
                  )
            )
          )
          .subscribe(this.store$);
      }

      return zip(
        ...load.map(loadDocumentParams =>
          this.store$.pipe(
            select(isCollectionLoaded(loadDocumentParams.path)),
            filter(loaded => !!loaded),
            first(),
            timeout(5000),
            catchError(err => {
              if (err instanceof TimeoutError) {
                return this.store$.pipe(
                  select(isCollectionLoaded(loadDocumentParams.path)),
                  first(),
                  map(
                    isLoaded =>
                      isLoaded ||
                      this.router.createUrlTree(['/', 'documents', '404'])
                  )
                );
              }
            })
          )
        )
      ).pipe(map(loaded => loaded.find(l => l !== true) || true));
    });
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.canActivate(childRoute, state);
  }
}
